import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Button, Modal, Row, Col, Form, OverlayTrigger, Tooltip as RBTooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdEdit, MdDelete, MdAddCircle, MdFilterList, MdVisibility } from "react-icons/md";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import loading from "../../assets/images/common/loading.gif";
import { fetchDataRead } from "../../Utility";
import * as XLSX from "xlsx";
import Tooltip from "@mui/material/Tooltip";
import AppDataGrid from "../../components/common/AppDataGrid";

const Employees = () => {
    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const [departments, setDepartments] = useState([]);
    const [states, setStates] = useState([]);
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [empRecords, setEmpRecords] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};
    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

    useEffect(() => {
        setIsLoading(true);
        fetchDataRead("/employees/", setEmpRecords, userObj.school_id).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const handleShowFilterModal = () => {
        if (states.length === 0) {
            fetchDataRead("/states/", setStates, userObj.school_id);
        }
        if (departments.length === 0) {
            fetchDataRead("/department/", setDepartments, userObj.school_id);
        }
        setShowFilterModal(true);
    };
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState("");
    const [viewData, setViewData] = useState(null);

    const fmtDate = (d) => {
        if (!d) return "—";
        let dt = null;
        if (d instanceof Date) {
            dt = d;
        } else if (typeof d === "number") {
            dt = new Date(d);
        } else if (typeof d === "string") {
            const plain = /^\d{4}-\d{2}-\d{2}$/.test(d);
            dt = new Date(plain ? d + "T00:00:00" : d);
        }
        if (!dt || isNaN(dt)) return String(d);
        const dd = String(dt.getDate()).padStart(2, "0");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const yyyy = dt.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };

    const maskTail = (s, visible = 4) => {
        const v = String(s || "");
        if (!v) return "—";
        if (v.length <= visible) return v;
        return "•".repeat(Math.max(0, v.length - visible)) + v.slice(-visible);
    };
    const genderLabel = (g) =>
        ({ M: "Male", F: "Female", O: "Other" }[String(g || "").toUpperCase()] || "");
    const initials = (first, last) =>
        [first, last].filter(Boolean).map((x) => x[0]).join("").toUpperCase() || "EMP";

    const handleViewClick = async (emp_id) => {
        setShowViewModal(true);
        setViewLoading(true);
        setViewError("");
        setViewData(null);
        try {
            const apiRoot = baseUrl || "http://127.0.0.1:5000";
            const url = `${apiRoot}/full-details/${emp_id}`;
            const { data } = await axios.get(url, { headers: { "Content-Type": "application/json" } });
            setViewData(data || null);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Failed to load employee details";
            setViewError(msg);
        } finally {
            setViewLoading(false);
        }
    };

    const handleEditClick = (emp_id) => {
        const row = empRecords?.find(e => String(e.emp_id) === String(emp_id));
        navigate("/AddEmployeeDetails", { state: { emp_id: Number(emp_id), empData: row || null } });
    };

    const handleDeleteClick = async (emp_id) => {
        if (!window.confirm("Are you sure you want to change the status?")) return;
        try {
            const response = await axios.post(
                `${baseUrl}/employedelete/`,
                { emp_id, lastmodifiedby: userObj.user_id },
                { headers: { "Content-Type": "application/json" } }
            );
            if (response.status >= 200 && response.status < 300) {
                toast.success("Record set to InActive");
                fetchDataRead("/employees/", setEmpRecords, userObj.school_id);
            } else {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to update status");
        }
    };

    const maritalLabel = (val) => {
        const v = String(val ?? '').trim().toUpperCase();
        if (!v) return '';
        if (v === 'Y') return 'Married';
        if (v === 'N') return 'Single';
        return '';
    };

    const ViewEmployeeContent = ({ data }) => {
        if (!data) return <div>No details found.</div>;

        const basic = data.basic || {};
        const salary =
            (data.salary || []).find(
                (s) => String(s.is_current || "").toUpperCase() === "Y"
            ) || (data.salary || [])[0] || null;

        const banks = data.bank_details || [];
        const educ = data.education_details || [];
        const exps = data.experience_details || [];

        // Family data (new)
        const family = data.parents || {};
        const kids = Array.isArray(data.children) ? data.children : [];

        const fullName = [basic.firstname, basic.surname].filter(Boolean).join(" ");

        const yesNo = (v) =>
            v == null
                ? "—"
                : String(v).trim().toUpperCase() === "Y"
                    ? "Yes"
                    : "No";

        return (
            <div className="view-emp compact">
                {/* Header */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "44px 1fr",
                        columnGap: "0.5rem",
                        alignItems: "center",
                    }}
                >
                    <span
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            background: "#eef2ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 14,
                        }}
                        title={fullName || "Employee"}
                    >
                        {initials(basic?.firstname, basic?.surname)}
                    </span>

                    <h6 style={{ fontSize: "1rem", gridColumn: "2" }}>
                        {fullName || "—"}{" "}
                        {basic?.emp_code ? (
                            <small className="text-muted">({basic.emp_code})</small>
                        ) : null}
                    </h6>

                    <span className="text-muted" style={{ fontSize: ".9rem", gridColumn: "2" }}>
                        {basic?.email || "—"}
                    </span>

                    <span className="text-muted" style={{ fontSize: ".9rem", gridColumn: "2" }}>
                        {basic?.phonenumber || "—"}
                        {basic?.city ? ` · ${basic.city}` : ""}
                    </span>
                </div>

                {/* Basic Details */}
                <div className="cardish mb-1">
                    <h6 className="sectionTitle">Basic Details</h6>
                    <Row className="gy-2">
                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Gender</small>
                            <span>{genderLabel(basic.gender) || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Marital Status</small>
                            <span>{maritalLabel(basic.is_married) || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">DOB</small>
                            <span>{fmtDate(basic.dob) || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">DOJ</small>
                            <span>{fmtDate(basic.doj) || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">DOR</small>
                            <span>{fmtDate(basic.dor) || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">UAN</small>
                            <span>{basic.uan || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Aadhar</small>
                            <span>{basic.aadhar || "—"}</span>
                        </Col>

                        <Col xs={12} sm={6} md={3} className="d-flex align-items-center gap-2">
                            <small className="text-muted">PF No.</small>
                            <span>{basic.pf_no || "—"}</span>
                        </Col>

                        <Col xs={12} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Address</small>
                            <span className="text-break">{basic.address || "—"}</span>
                        </Col>
                    </Row>
                </div>

                {/* Family Details */}
                <div className="cardish mb-1">
                    <h6 className="sectionTitle">Family Details</h6>
                    <Row className="gy-2">
                        {/* Father */}
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Father Name</small>
                            <span>{family.father_name || basic.father_name || "—"}</span>
                        </Col>
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Father Phone</small>
                            <span>{family.father_phone || basic.father_phone || "—"}</span>
                        </Col>
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Father Occupation</small>
                            <span>{family.father_occupation || basic.father_occupation || "—"}</span>
                        </Col>

                        {/* Mother */}
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Mother Name</small>
                            <span>{family.mother_name || basic.mother_name || "—"}</span>
                        </Col>
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Mother Phone</small>
                            <span>{family.mother_phone || basic.mother_phone || "—"}</span>
                        </Col>
                        <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Mother Occupation</small>
                            <span>{family.mother_occupation || basic.mother_occupation || "—"}</span>
                        </Col>

                        {/* Spouse */}
                        {(String(family.is_married || basic.is_married || "")
                            .trim()
                            .toUpperCase() === "Y") ||
                            family.spouse_name ||
                            basic.spouse_name ? (
                            <>
                                <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                                    <small className="text-muted">Spouse Name</small>
                                    <span>{family.spouse_name || basic.spouse_name || "—"}</span>
                                </Col>
                                <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                                    <small className="text-muted">Spouse Phone</small>
                                    <span>{family.spouse_phone || basic.spouse_phone || "—"}</span>
                                </Col>
                                <Col xs={12} sm={6} md={4} className="d-flex align-items-center gap-2">
                                    <small className="text-muted">Spouse Occupation</small>
                                    <span>{family.spouse_occupation || basic.spouse_occupation || "—"}</span>
                                </Col>
                            </>
                        ) : null}

                        {/* Children */}
                        <Col xs={12} className="d-flex align-items-center gap-2">
                            <small className="text-muted">Children</small>
                            <span>
                                {kids.length
                                    ? kids
                                        .map(
                                            (c) =>
                                                `${c.child_name || "—"}${c.child_gender ? ` (${genderLabel(c.child_gender)})` : ""
                                                }`
                                        )
                                        .join(", ")
                                    : "—"}
                            </span>
                        </Col>
                    </Row>
                </div>

                {/* Salary */}
                <div className="cardish mb-2">
                    <h6 className="sectionTitle">Salary</h6>
                    {salary ? (
                        <Row className="gy-1">
                            <Col md={4} className="d-flex align-items-center gap-2">
                                <small className="text-muted">CTC</small>
                                <span>{salary?.ctc ?? "—"}</span>
                            </Col>

                            <Col md={4} className="d-flex align-items-center gap-2">
                                <small className="text-muted">Gross</small>
                                <span>{salary?.gross_salary ?? "—"}</span>
                            </Col>

                            <Col md={4} className="d-flex align-items-center gap-2">
                                <small className="text-muted">Is Current</small>
                                <span>{yesNo(salary?.is_current)}</span>
                            </Col>

                            <Col md={4} className="d-flex align-items-center gap-2">
                                <small className="text-muted">Effective From</small>
                                <span>{fmtDate(salary?.effective_from) || "—"}</span>
                            </Col>

                            <Col md={4} className="d-flex align-items-center gap-2">
                                <small className="text-muted">Effective To</small>
                                <span>{fmtDate(salary?.effective_to) || "—"}</span>
                            </Col>

                            <Col md={12} className="d-flex align-items-center gap-2">
                                <small className="text-muted">Revision Reason</small>
                                <span className="text-break">{salary?.revision_reason || "—"}</span>
                            </Col>
                        </Row>
                    ) : (
                        <div className="text-muted">No salary records.</div>
                    )}
                </div>

                {/* Bank Details */}
                <div className="cardish mb-2">
                    <h6 className="sectionTitle">Bank Details</h6>
                    {banks.length ? (
                        <div className="table-responsive">
                            <table className="receipt-table">
                                <thead>
                                    <tr>
                                        <th>Account Name</th>
                                        <th>Account No.</th>
                                        <th>IFSC</th>
                                        <th>Bank</th>
                                        <th>Branch</th>
                                        <th>Location</th>
                                    </tr>
                                </thead>
                                <tbody className="viewemp">
                                    {banks.map((b) => (
                                        <tr key={b.emp_bank_details_id}>
                                            <td>{b.account_name || "—"}</td>
                                            <td title={b.account_number || ""}>{maskTail(b.account_number)}</td>
                                            <td>{b.ifsc_code || "—"}</td>
                                            <td>{b.bank_name || "—"}</td>
                                            <td>{b.branch_name || "—"}</td>
                                            <td>{b.location || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted">No bank details.</div>
                    )}
                </div>

                {/* Education */}
                <div className="cardish mb-2">
                    <h6 className="sectionTitle">Education</h6>
                    {educ.length ? (
                        <div className="table-responsive">
                            <table className="receipt-table">
                                <thead>
                                    <tr>
                                        <th>Degree</th>
                                        <th>Field</th>
                                        <th>Institution</th>
                                        <th>Board / Univ</th>
                                        <th>Year</th>
                                        <th>% </th>
                                        <th>CGPA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {educ.map((e) => (
                                        <tr key={e.emp_education_id}>
                                            <td>{e.degree_name || "—"}</td>
                                            <td>{e.field_of_study || "—"}</td>
                                            <td>{e.institution_name || "—"}</td>
                                            <td>{e.board_university || "—"}</td>
                                            <td>{e.graduation_year || "—"}</td>
                                            <td>{e.percentage || "-"}</td>
                                            <td>{e.cgpa || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted">No education records.</div>
                    )}
                </div>

                {/* Experience */}
                <div className="cardish mb-1">
                    <h6 className="sectionTitle">Experience</h6>
                    {exps.length ? (
                        <div className="table-responsive">
                            <table className="receipt-table">
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Location</th>
                                        <th>Start</th>
                                        <th>End</th>
                                        <th>Years</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exps.map((x) => (
                                        <tr key={x.emp_experience_id}>
                                            <td>{x.organization_name || "—"}</td>
                                            <td>{x.location || "—"}</td>
                                            <td>{fmtDate(x.start_date)}</td>
                                            <td>{fmtDate(x.end_date)}</td>
                                            <td>{x.years_of_experience ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted">No experience records.</div>
                    )}
                </div>
            </div>
        );
    };

    const columns = [
        {
            field: "emp_code",
            headerName: "Emp Code",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "emp_name",
            headerName: "Name",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
            hide: !empRecords?.some?.(r => "emp_name" in r),
        },
        {
            field: "uan",
            headerName: "UAN",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "aadhar",
            headerName: "Aadhar",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "phonenumber",
            headerName: "Phone Number",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "dept_name",
            headerName: "Department",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "pf_no",
            headerName: "PF No",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "dob",
            headerName: "DOB",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={fmtDate(params.value) || ""}>
                    <span>{fmtDate(params.value) || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "designation_code",
            headerName: "Designation",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "employee_type",
            headerName: "Employee Type",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "shift_code",
            headerName: "Shift",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "reporting_to",
            headerName: "Reporting To",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "dor",
            headerName: "DOR",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={fmtDate(params.value) || ""}>
                    <span>{fmtDate(params.value) || ""}</span>
                </Tooltip>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            sortable: true,
            hide: !empRecords?.some?.(r => "status" in r),
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span>{params.value || ""}</span>
                </Tooltip>
            ),
        },
        ...(canSubmit
            ? [
                {
                    field: "actions",
                    headerName: "Actions",
                    flex: 1,
                    sortable: false,
                    renderCell: (params) =>
                        params.row.designation_code !== "No Records Found" ? (
                            <div className="tableActions">
                                <Tooltip title="View" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleViewClick(params.row.emp_id)}
                                    >
                                        <MdVisibility />
                                    </span>
                                </Tooltip>

                                {canSubmit && (
                                    <>
                                        <Tooltip title="Edit" arrow>
                                            <span
                                                className="commonActionIcons"
                                                onClick={() => handleEditClick(params.row.emp_id)}
                                            >
                                                <MdEdit />
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Delete" arrow>
                                            <span
                                                className="commonActionIcons"
                                                onClick={() => handleDeleteClick(params.row.emp_id)}
                                            >
                                                <MdDelete />
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        ) : null,
                },
            ]
            : []),
    ];

    const searchableColumns = [
        (r) => r.emp_code,
        (r) => r.emp_name,
        (r) => r.uan,
        (r) => r.aadhar,
        (r) => r.pf_no,
        (r) => r.designation_code,
        (r) => r.dept_name,
        (r) => r.employee_type,
        (r) => r.shift_code,
        (r) => r.reporting_to,
        (r) => r.cash_bank_code,
    ];

    // ✅ safer + normalized search (no runtime error on undefined)
    const filteredRecords = (empRecords || []).filter((row) =>
        searchableColumns.some((selector) => {
            const value = selector(row);
            const normalizedValue = String(value || "")
                .toLowerCase()
                .replace(/[-\s]+/g, "");
            const normalizedQuery = searchQuery
                .toLowerCase()
                .replace(/[-\s]+/g, "");
            return normalizedValue.includes(normalizedQuery);
        })
    );

    const [filter, setFilter] = useState({
        emp_id: 0,
        emp_code: "",
        aadhar: "",
        uan: "",
        pf_no: "",
        designation_code: "",
        deptid: 0,
        employee_type: "",
        is_married: "",
        shift_code: "",
        reporting_to: "",
        dob: "",
        dor: "",
        school_id: userObj.school_id,
        action: "FILTER",
    });

    const handleFilterSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            emp_code: filter.emp_code?.trim() || null,
            aadhar: filter.aadhar?.trim() || null,
            uan: filter.uan?.trim() || null,
            pf_no: filter.pf_no?.trim() || null,
            designation_code: filter.designation_code?.trim() || null,
            deptid: Number(filter.deptid) || null,
            employee_type: filter.employee_type || null,
            is_married: filter.is_married || null,
            shift_code: filter.shift_code?.trim() || null,
            reporting_to: filter.reporting_to?.trim() || null,
            dob: filter.dob || null,
            dor: filter.dor || null,
            school_id: userObj.school_id,
            action: "FILTER",
        };

        try {
            const response = await axios.post(`${baseUrl}/employees/`, payload, {
                headers: { "Content-Type": "application/json" },
            });
            const data = response.data || [];
            setEmpRecords(Array.isArray(data) ? data : []);
            setShowFilterModal(false);
        } catch (error) {
            console.error("Error fetching filtered data:", error);
            if (error.response && error.response.status === 404) {
                setEmpRecords([]);
            } else {
                toast.error("Failed to fetch filtered data");
            }
        }
    };

    const handleFilterClear = async () => {
        setFilter({
            emp_id: 0,
            emp_code: "",
            aadhar: "",
            uan: "",
            pf_no: "",
            designation_code: "",
            deptid: 0,
            employee_type: "",
            is_married: "",
            shift_code: "",
            reporting_to: "",
            dob: "",
            dor: "",
            school_id: userObj.school_id,
            action: "FILTER",
        });
        fetchDataRead("/employees/", setEmpRecords, userObj.school_id);
    };

    const handleSearchChange = (event) => {
        fetchDataRead("/employees/", setEmpRecords, userObj.school_id);
        setSearchQuery(event.target.value);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const validExtensions = [".xlsx", ".xls"];
            const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf("."));
            if (!validExtensions.includes(extension)) {
                toast.warning("Please upload a valid Excel file (.xlsx or .xls)");
                return;
            }
            setFile(selectedFile);
        }
    };

    const handlePrint = () => {
        const printContents = document.getElementById('printArea')?.innerHTML;
        if (!printContents) return;

        const newWindow = window.open('', '', 'height=600,width=800');
        newWindow.document.write(`
        <html>
        <head>
            <title>Employee Details</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    padding: 40px;
                    background: #fff;
                    color: #000;
                    font-size: 14px;
                    line-height: 1.6;
                }

                h6.sectionTitle {
                    font-size: 16px;
                    margin: 24px 0 10px;
                    padding-bottom: 4px;
                    border-bottom: 2px solid #000;
                }

                .view-emp {
                    width: 100%;
                }

                .row, .gy-2, .gy-1 {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }

                .d-flex {
                    display: flex;
                    align-items: center;
                }

                .align-items-center {
                    align-items: center;
                }

                .gap-2 {
                    gap: 0.5rem;
                }

                .col, [class*="Col"] {
                    flex: 1 0 25%;
                    max-width: 25%;
                    box-sizing: border-box;
                    padding: 5px;
                }

                .col-full {
                    flex: 0 0 100%;
                    max-width: 100%;
                    padding: 5px;
                }

                .text-muted {
                    color: #555;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 10px;
                    font-size: 13px;
                }

                th, td {
                    border: 1px solid #999;
                    padding: 8px;
                    text-align: left;
                }

                th {
                    background: #f0f0f0;
                    font-weight: bold;
                }

                .text-break {
                    word-break: break-word;
                }

                .avatar {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: #eef2ff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 16px;
                    margin-bottom: 10px;
                }

                .headerBlock {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 20px;
                }
            </style>
        </head>
        <body>
            ${printContents}
        </body>
        </html>
    `);
        newWindow.document.close();
        newWindow.focus();
        newWindow.print();
        newWindow.close();
    };


    return (
        <div className="pageBody">
            <ToastContainer />

            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Employees</h6>
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <div className="fileUploadPart" style={{ gap: 6 }}></div>
                        <OverlayTrigger placement="top" overlay={<RBTooltip id="tooltip-top">Filter</RBTooltip>}>
                            <Button className="secondaryBtn" variant="Secondary" onClick={handleShowFilterModal}>
                                <span>
                                    <MdFilterList />
                                </span>
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<RBTooltip id="tooltip-top">Add</RBTooltip>}>
                            <Button
                                className="primaryBtn"
                                disabled={!canSubmit}
                                variant="primary"
                                onClick={() => navigate("/AddEmployeeDetails")}
                            >
                                <span>
                                    <MdAddCircle />
                                </span>
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={filteredRecords} 
                            columns={columns}
                            getRowId={(row) => row.emp_id}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={550}
                        />
                    )}
                </div>
            </div>

            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Form id="filterForm" onSubmit={handleFilterSubmit}>
                        <Row>
                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="emp_code">
                                        <Form.Label>Employee Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.emp_code}
                                            onChange={(e) => setFilter({ ...filter, emp_code: e.target.value.trim() })}
                                            maxLength={30}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="aadhar">
                                        <Form.Label>Aadhar</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.aadhar}
                                            onChange={(e) => setFilter({ ...filter, aadhar: e.target.value.replace(/\D/g, "") })}
                                            maxLength={12}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="uan">
                                        <Form.Label>UAN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.uan}
                                            onChange={(e) => setFilter({ ...filter, uan: e.target.value.trim() })}
                                            maxLength={20}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="pf_no">
                                        <Form.Label>PF No.</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.pf_no}
                                            onChange={(e) => setFilter({ ...filter, pf_no: e.target.value.trim() })}
                                            maxLength={30}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="designation_code">
                                        <Form.Label>Designation Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.designation_code}
                                            onChange={(e) => setFilter({ ...filter, designation_code: e.target.value.trim() })}
                                            maxLength={30}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="deptid">
                                        <Form.Label>Department</Form.Label>
                                        <Form.Select
                                            value={filter.deptid}
                                            onChange={(e) => setFilter({ ...filter, deptid: e.target.value })}
                                        >
                                            <option value="">Select Department</option>
                                            {(departments || [])
                                                .filter((d) => d.is_active === "Active")
                                                .map((d) => (
                                                    <option key={d.dept_id} value={d.dept_id}>
                                                        {d.dept_name}
                                                    </option>
                                                ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="employee_type">
                                        <Form.Label>Employee Type</Form.Label>
                                        <Form.Select
                                            value={filter.employee_type}
                                            onChange={(e) => setFilter({ ...filter, employee_type: e.target.value })}
                                        >
                                            <option value="">Select</option>
                                            <option value="Staff">Staff</option>
                                            <option value="Sub Staff">Sub Staff</option>
                                            <option value="contract">contract</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="is_married">
                                        <Form.Label>Marital Status</Form.Label>
                                        <Form.Select
                                            value={filter.is_married}
                                            onChange={(e) => setFilter({ ...filter, is_married: e.target.value })}
                                        >
                                            <option value="">Select</option>
                                            <option value="Y">Married</option>
                                            <option value="N">Unmarried</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="shift_code">
                                        <Form.Label>Shift Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.shift_code}
                                            onChange={(e) => setFilter({ ...filter, shift_code: e.target.value.trim() })}
                                            maxLength={20}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="reporting_to">
                                        <Form.Label>Reporting To</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={filter.reporting_to}
                                            onChange={(e) => setFilter({ ...filter, reporting_to: e.target.value.trim() })}
                                            maxLength={50}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="dob">
                                        <Form.Label>Date of Birth</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filter.dob}
                                            onChange={(e) => setFilter({ ...filter, dob: e.target.value })}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>

                            <Col xs={12}>
                                <div className="commonInput">
                                    <Form.Group controlId="dor">
                                        <Form.Label>Date of Retirement (or Relieving)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={filter.dor}
                                            onChange={(e) => setFilter({ ...filter, dor: e.target.value })}
                                        />
                                    </Form.Group>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <div>
                        <Button variant="secondary" className="btn-info clearBtn" onClick={handleFilterClear}>
                            Reset
                        </Button>
                    </div>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            className="btn-success primaryBtn"
                            form="filterForm"
                            onClick={handleCloseFilterModal}
                        >
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                centered
                size="xl"
                className="viewEmployeeModal"
            >
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Employee Details</Modal.Title>
                </Modal.Header>

                <Modal.Body className="modalBodyScrollable">
                    {viewLoading ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : viewError ? (
                        <div className="alert alert-danger mb-0">{viewError}</div>
                    ) : (
                        <div id="printArea">
                            <ViewEmployeeContent data={viewData} />
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="modalFooterFixed gap-2">
                    <Button
                        variant="secondary"
                        className="btn-success primaryBtn"
                        onClick={handlePrint}
                    >
                        Print
                    </Button>
                    <Button
                        variant="secondary"
                        className="btn-danger secondaryBtn"
                        onClick={() => setShowViewModal(false)}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Employees;
